import React, { useMemo, useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, UsersIcon, FilterIcon, SearchIcon, ClockIcon, DollarSignIcon, CheckIcon, LayoutGridIcon, LayoutListIcon, TicketIcon, PlusIcon, ChevronLeftIcon, ChevronRightIcon, MailIcon, XIcon, DownloadIcon, QrCodeIcon } from 'lucide-react';
import { parseISO, isBefore, isAfter, format } from 'date-fns';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
// Types
type MainTab = 'find' | 'myevents' | 'calendar' | 'tickets';
type FindSubTab = 'open' | 'upcoming' | 'past';
type ViewMode = 'list' | 'grid';
type ApplicationStatus = 'applied' | 'confirmed' | 'waitlisted' | 'declined' | 'past';
type TicketStatus = 'valid' | 'used' | 'expired';
interface EventOperator {
  name: string;
  organization: string;
  email: string;
}
interface VendorEvent {
  id: string;
  title: string;
  date: string;
  applicationDeadline: string;
  location: string;
  expectedAttendance: number;
  boothPriceRange: string;
  eventType: string;
  image: string;
  interestedVendors: number;
  description: string;
  operator: EventOperator;
  region: string;
}
interface MyEvent {
  id: string;
  eventId: string;
  title: string;
  date: string;
  location: string;
  status: ApplicationStatus;
  boothType: string;
  totalCost: number;
  image: string;
}
interface EventTicket {
  id: string;
  eventTitle: string;
  ticketType: string;
  purchaseDate: string;
  price: number;
  status: TicketStatus;
  eventDate: string;
  image: string;
}
// Mock Data - Vendor Events
const vendorEvents: VendorEvent[] = [{
  id: 'v1',
  title: 'Northwest Book Festival',
  date: '2026-03-15',
  applicationDeadline: '2026-03-01',
  location: 'Seattle Convention Center, Seattle, WA',
  expectedAttendance: 5000,
  boothPriceRange: '$100-$400',
  eventType: 'Book Fair',
  image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=800',
  interestedVendors: 32,
  description: 'Annual celebration featuring 100+ authors, panel discussions, and direct sales opportunities. Perfect for indie authors looking to expand their West Coast presence.',
  operator: {
    name: 'Sarah Thompson',
    organization: 'Pacific Literary Events',
    email: 'sarah@pacificlit.com'
  },
  region: 'West Coast'
}, {
  id: 'v2',
  title: 'Fantasy & Sci-Fi Con',
  date: '2026-04-20',
  applicationDeadline: '2026-03-10',
  location: 'Portland Exhibition Hall, Portland, OR',
  expectedAttendance: 3000,
  boothPriceRange: '$200-$500',
  eventType: 'Convention',
  image: 'https://images.unsplash.com/photo-1519408299519-b7a0274f7d67?w=800',
  interestedVendors: 45,
  description: 'Genre-specific convention focusing on fantasy and science fiction books, games, and media. Highly engaged audience of dedicated genre readers.',
  operator: {
    name: 'Michael Roberts',
    organization: 'Genre Conventions LLC',
    email: 'michael@genrecon.com'
  },
  region: 'West Coast'
}, {
  id: 'v3',
  title: 'Romance Readers Retreat',
  date: '2026-04-14',
  applicationDeadline: '2026-03-10',
  location: 'Grand Hotel Ballroom, Denver, CO',
  expectedAttendance: 1500,
  boothPriceRange: '$225-$400',
  eventType: 'Retreat',
  image: 'https://images.unsplash.com/photo-1515169067868-5387ec356754?w=800',
  interestedVendors: 39,
  description: 'Intimate event for romance authors and readers with signing sessions, reader interaction opportunities, and networking events.',
  operator: {
    name: 'Jennifer Blake',
    organization: 'Romance Events Co',
    email: 'jen@romanceevents.com'
  },
  region: 'Midwest'
}, {
  id: 'v4',
  title: 'Literary Arts Festival',
  date: '2026-05-15',
  applicationDeadline: '2026-04-01',
  location: 'Downtown Arts Center, Chicago, IL',
  expectedAttendance: 4000,
  boothPriceRange: '$275-$600',
  eventType: 'Festival',
  image: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?w=800',
  interestedVendors: 27,
  description: 'Prestigious event celebrating literary fiction, poetry, and non-fiction with panel discussions, readings, and book signings.',
  operator: {
    name: 'David Chen',
    organization: 'Chicago Literary Foundation',
    email: 'david@chilit.org'
  },
  region: 'Midwest'
}, {
  id: 'v5',
  title: 'Independent Publishers Expo',
  date: '2026-06-10',
  applicationDeadline: '2026-04-28',
  location: 'Tech Campus Conference Center, Austin, TX',
  expectedAttendance: 2500,
  boothPriceRange: '$175-$550',
  eventType: 'Expo',
  image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800',
  interestedVendors: 22,
  description: 'Focused on independent publishing with workshops, networking, and sales opportunities for indie authors and small presses.',
  operator: {
    name: 'Amanda Torres',
    organization: 'Indie Pub Network',
    email: 'amanda@indiepub.net'
  },
  region: 'South'
}, {
  id: 'v6',
  title: "Children's Book Weekend",
  date: '2026-04-05',
  applicationDeadline: '2026-03-05',
  location: 'City Library Plaza, San Francisco, CA',
  expectedAttendance: 2000,
  boothPriceRange: '$150-$350',
  eventType: 'Book Fair',
  image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800',
  interestedVendors: 18,
  description: "Family-friendly event focused on children's literature with readings, activities, and author signing opportunities.",
  operator: {
    name: 'Lisa Park',
    organization: 'SF Kids Lit',
    email: 'lisa@sfkidslit.org'
  },
  region: 'West Coast'
}, {
  id: 'v7',
  title: 'Thriller Writers Summit',
  date: '2026-05-22',
  applicationDeadline: '2026-04-15',
  location: 'Midtown Conference Center, New York, NY',
  expectedAttendance: 1800,
  boothPriceRange: '$300-$700',
  eventType: 'Conference',
  image: 'https://images.unsplash.com/photo-1587876931567-564ce588bfbd?w=800',
  interestedVendors: 35,
  description: 'Premier gathering for thriller and suspense authors featuring craft workshops, agent pitches, and reader meet-and-greets.',
  operator: {
    name: 'Robert Kane',
    organization: 'Thriller Writers Association',
    email: 'robert@thrillerwriters.org'
  },
  region: 'East Coast'
}, {
  id: 'v8',
  title: 'Southern Book Lovers Fair',
  date: '2026-06-20',
  applicationDeadline: '2026-05-15',
  location: 'Music City Center, Nashville, TN',
  expectedAttendance: 3500,
  boothPriceRange: '$150-$450',
  eventType: 'Book Fair',
  image: 'https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?w=800',
  interestedVendors: 41,
  description: 'Celebrating Southern literature and storytelling with a focus on regional authors and diverse voices.',
  operator: {
    name: 'Mary Johnson',
    organization: 'Southern Lit Society',
    email: 'mary@southernlit.com'
  },
  region: 'South'
}, {
  id: 'v9',
  title: 'Horror & Dark Fiction Night',
  date: '2026-10-25',
  applicationDeadline: '2026-09-15',
  location: 'Hawthorne Hotel, Salem, MA',
  expectedAttendance: 800,
  boothPriceRange: '$125-$300',
  eventType: 'Signing',
  image: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
  interestedVendors: 28,
  description: 'Atmospheric signing event in historic Salem, perfect for horror, gothic, and dark fiction authors.',
  operator: {
    name: 'Edgar Blackwood',
    organization: 'Dark Pages Events',
    email: 'edgar@darkpages.com'
  },
  region: 'East Coast'
}, {
  id: 'v10',
  title: 'Cozy Mystery Tea Party',
  date: '2026-05-10',
  applicationDeadline: '2026-04-01',
  location: 'Historic Inn, Savannah, GA',
  expectedAttendance: 600,
  boothPriceRange: '$100-$250',
  eventType: 'Retreat',
  image: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?w=800',
  interestedVendors: 15,
  description: 'Charming afternoon event for cozy mystery authors and their devoted readers. Includes tea service and intimate signing sessions.',
  operator: {
    name: 'Patricia Wells',
    organization: 'Cozy Corner Events',
    email: 'patricia@cozycorner.com'
  },
  region: 'South'
}, {
  id: 'v11',
  title: 'Indie Author Showcase',
  date: '2026-07-12',
  applicationDeadline: '2026-06-01',
  location: 'Convention Center, Denver, CO',
  expectedAttendance: 1200,
  boothPriceRange: '$125-$350',
  eventType: 'Expo',
  image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800',
  interestedVendors: 33,
  description: 'Dedicated showcase for self-published and indie authors with marketing workshops and reader engagement opportunities.',
  operator: {
    name: 'Chris Martinez',
    organization: 'Indie Author Alliance',
    email: 'chris@indieauthors.org'
  },
  region: 'Midwest'
}, {
  id: 'v12',
  title: 'Book Launch Gala',
  date: '2026-04-30',
  applicationDeadline: '2026-03-30',
  location: 'The Roosevelt, Los Angeles, CA',
  expectedAttendance: 500,
  boothPriceRange: '$200-$500',
  eventType: 'Launch Party',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800',
  interestedVendors: 12,
  description: 'Elegant evening gala celebrating new releases with networking opportunities, media coverage, and VIP reader access.',
  operator: {
    name: 'Victoria Lane',
    organization: 'LA Literary Events',
    email: 'victoria@lalitevents.com'
  },
  region: 'West Coast'
}];
// Mock Data - My Events (applied)
const myEvents: MyEvent[] = [{
  id: 'me1',
  eventId: 'v1',
  title: 'Northwest Book Festival',
  date: '2026-03-15',
  location: 'Seattle Convention Center, Seattle, WA',
  status: 'confirmed',
  boothType: 'Premium Corner Booth',
  totalCost: 400,
  image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=800'
}, {
  id: 'me2',
  eventId: 'v3',
  title: 'Romance Readers Retreat',
  date: '2026-04-14',
  location: 'Grand Hotel Ballroom, Denver, CO',
  status: 'applied',
  boothType: 'Author Table',
  totalCost: 225,
  image: 'https://images.unsplash.com/photo-1515169067868-5387ec356754?w=800'
}, {
  id: 'me3',
  eventId: 'v4',
  title: 'Literary Arts Festival',
  date: '2026-05-15',
  location: 'Downtown Arts Center, Chicago, IL',
  status: 'waitlisted',
  boothType: 'Standard Author Space',
  totalCost: 275,
  image: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?w=800'
}, {
  id: 'me4',
  eventId: 'v7',
  title: 'Thriller Writers Summit',
  date: '2026-05-22',
  location: 'Midtown Conference Center, New York, NY',
  status: 'declined',
  boothType: 'Premium Booth',
  totalCost: 500,
  image: 'https://images.unsplash.com/photo-1587876931567-564ce588bfbd?w=800'
}, {
  id: 'me5',
  eventId: 'v0',
  title: 'Winter Book Bazaar 2025',
  date: '2025-12-10',
  location: 'Portland Convention Center, Portland, OR',
  status: 'past',
  boothType: 'Author Table',
  totalCost: 175,
  image: 'https://images.unsplash.com/photo-1519408299519-b7a0274f7d67?w=800'
}];
// Mock Data - Tickets
const eventTickets: EventTicket[] = [{
  id: 't1',
  eventTitle: 'Author Marketing Masterclass',
  ticketType: 'VIP Access',
  purchaseDate: '2026-01-15',
  price: 149,
  status: 'valid',
  eventDate: '2026-04-05',
  image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800'
}, {
  id: 't2',
  eventTitle: 'Publishing Industry Summit',
  ticketType: 'General Admission',
  purchaseDate: '2026-02-01',
  price: 75,
  status: 'valid',
  eventDate: '2026-05-20',
  image: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?w=800'
}, {
  id: 't3',
  eventTitle: 'Holiday Author Mixer 2025',
  ticketType: 'Standard',
  purchaseDate: '2025-11-20',
  price: 45,
  status: 'used',
  eventDate: '2025-12-15',
  image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800'
}];
const VendorEventsHub = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // State
  const [activeTab, setActiveTab] = useState<MainTab>('find');
  const [findSubTab, setFindSubTab] = useState<FindSubTab>('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [interestedEvents, setInterestedEvents] = useState<string[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);
  // Filter state
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  // Helper functions
  const getDaysRemaining = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const getStatusBadgeClasses = (status: ApplicationStatus) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'applied':
        return 'bg-blue-100 text-blue-800';
      case 'waitlisted':
        return 'bg-amber-100 text-amber-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'past':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getTicketStatusBadge = (status: TicketStatus) => {
    switch (status) {
      case 'valid':
        return 'bg-green-100 text-green-800';
      case 'used':
        return 'bg-gray-100 text-gray-600';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Categorize events
  const categorizedEvents = useMemo(() => {
    const today = new Date();
    return {
      open: vendorEvents.filter(e => !isBefore(parseISO(e.applicationDeadline), today)),
      upcoming: vendorEvents.filter(e => {
        const deadline = parseISO(e.applicationDeadline);
        const eventDate = parseISO(e.date);
        return isBefore(deadline, today) && isAfter(eventDate, today);
      }),
      past: vendorEvents.filter(e => isBefore(parseISO(e.date), today))
    };
  }, []);
  // Filter events
  const filteredEvents = useMemo(() => {
    let events = categorizedEvents[findSubTab];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      events = events.filter(e => e.title.toLowerCase().includes(q) || e.location.toLowerCase().includes(q) || e.description.toLowerCase().includes(q) || e.eventType.toLowerCase().includes(q));
    }
    if (selectedEventTypes.length > 0) {
      events = events.filter(e => selectedEventTypes.includes(e.eventType));
    }
    if (selectedRegions.length > 0) {
      events = events.filter(e => selectedRegions.includes(e.region));
    }
    if (selectedPriceRange !== 'all') {
      events = events.filter(e => {
        const minPrice = parseInt(e.boothPriceRange.match(/\$(\d+)/)?.[1] || '0');
        if (selectedPriceRange === 'under200') return minPrice < 200;
        if (selectedPriceRange === '200to400') return minPrice >= 200 && minPrice <= 400;
        if (selectedPriceRange === 'over400') return minPrice > 400;
        return true;
      });
    }
    return events;
  }, [findSubTab, searchQuery, selectedEventTypes, selectedRegions, selectedPriceRange, categorizedEvents]);
  // Filter sections
  const filterSections: FilterSection[] = [{
    id: 'eventType',
    title: 'Event Type',
    type: 'checkbox',
    options: [{
      value: 'Book Fair',
      label: 'Book Fair'
    }, {
      value: 'Convention',
      label: 'Convention'
    }, {
      value: 'Festival',
      label: 'Festival'
    }, {
      value: 'Retreat',
      label: 'Retreat'
    }, {
      value: 'Expo',
      label: 'Expo'
    }, {
      value: 'Conference',
      label: 'Conference'
    }, {
      value: 'Signing',
      label: 'Signing'
    }, {
      value: 'Launch Party',
      label: 'Launch Party'
    }],
    value: selectedEventTypes,
    onChange: (val: string) => setSelectedEventTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'priceRange',
    title: 'Price Range',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Prices'
    }, {
      value: 'under200',
      label: 'Under $200'
    }, {
      value: '200to400',
      label: '$200 - $400'
    }, {
      value: 'over400',
      label: '$400+'
    }],
    value: selectedPriceRange,
    onChange: setSelectedPriceRange
  }, {
    id: 'region',
    title: 'Region',
    type: 'checkbox',
    options: [{
      value: 'West Coast',
      label: 'West Coast'
    }, {
      value: 'East Coast',
      label: 'East Coast'
    }, {
      value: 'Midwest',
      label: 'Midwest'
    }, {
      value: 'South',
      label: 'South'
    }],
    value: selectedRegions,
    onChange: (val: string) => setSelectedRegions(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const resetFilters = () => {
    setSelectedEventTypes([]);
    setSelectedPriceRange('all');
    setSelectedRegions([]);
    setSearchQuery('');
  };
  const toggleInterest = (eventId: string) => {
    setInterestedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  // Calendar helpers
  const getFirstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const getDaysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const formatDateString = (year: number, month: number, day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const getEventsForCalendarDate = (dateString: string) => {
    const myEventDates = myEvents.filter(e => e.date === dateString);
    const deadlines = vendorEvents.filter(e => e.applicationDeadline === dateString);
    return {
      myEvents: myEventDates,
      deadlines
    };
  };
  // Tabs config
  const mainTabs = [{
    id: 'find' as MainTab,
    label: 'Find Events',
    icon: SearchIcon
  }, {
    id: 'myevents' as MainTab,
    label: 'My Events',
    icon: CalendarIcon
  }, {
    id: 'calendar' as MainTab,
    label: 'Calendar',
    icon: CalendarIcon
  }, {
    id: 'tickets' as MainTab,
    label: 'Tickets',
    icon: TicketIcon
  }];
  const findSubTabs = [{
    id: 'open' as FindSubTab,
    label: `Open Applications (${categorizedEvents.open.length})`
  }, {
    id: 'upcoming' as FindSubTab,
    label: `Upcoming (${categorizedEvents.upcoming.length})`
  }, {
    id: 'past' as FindSubTab,
    label: `Past (${categorizedEvents.past.length})`
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-21813" data-mp="1kkfe2heutqlo">
      {/* Header */}
      <div className={`pt-8 pb-6 px-4 sm:px-6 lg:px-8 ${dk.surface} border-b ${dk.border}`} data-id="element-21814" data-mp="3r9k8jttzv8ir">
        <div className="max-w-7xl mx-auto" data-id="element-21815" data-mp="3mcan0p9q7h07">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8" data-id="element-21816" data-mp="3mddf30isbphp">
            <div className="flex items-center gap-3" data-id="element-21817" data-mp="21vb5apu7emmg">
              <CalendarIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-21818" data-mp="26r9p004l7ggj" />
              <div data-id="element-21819" data-mp="1s3omo8fjdl9o">
                <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-21820" data-mp="1s46r3sccr5so">
                  Events Hub
                </h1>
                <p className={`mt-1 ${dk.textSecondary}`} data-id="element-21821" data-mp="1x16we11do4iq">
                  Find events, manage applications, and track your schedule
                </p>
              </div>
            </div>
            <div className="w-full md:w-96" data-id="element-21822" data-mp="1o2l8dqd4zxak">
              <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Search events..." variant="pill" suggestions={[]} data-id="element-21823" data-mp="1nzgz4ektebd1" />
            </div>
          </div>

          {/* Main Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0" data-id="element-21824" data-mp="38hok9cqwsytr">
            {mainTabs.map(tab => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border ${isActive ? `${rt.bgDark} text-white border-transparent shadow-md` : `${dk.surface} ${dk.textSecondary} ${dk.border} hover:${dk.surfaceMuted}`}`} data-id="element-21825" data-mp="3203jahst4mhi">
                  <Icon className="h-4 w-4" data-id="element-21826" data-mp="3ktzc1nytzf75" />
                  {tab.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-21827" data-mp="3bxi0ijmk1fav">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-21828" data-mp="ccw77bng89wn">
          {/* Mobile Filter Toggle */}
          {activeTab === 'find' && <div className="lg:hidden mb-4" data-id="element-21829" data-mp="7j9uxkl7681d">
              <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-lg shadow-sm ${dk.card} ${dk.textPrimary}`} data-id="element-21830" data-mp="1q8w75qh7z1ml">
                <FilterIcon className="h-5 w-5 mr-2" data-id="element-21831" data-mp="1z7985gfmin5u" />
                Filters
              </button>
            </div>}

          {/* Sidebar - only show on Find Events tab */}
          {activeTab === 'find' && <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-21832" data-mp="2955qas6zaidg">
              <FilterSidebar title="Filters" sections={filterSections} onReset={resetFilters} searchable={false} data-id="element-21833" data-mp="2mx3inrcgzzmx" />
            </FilterSidebarWrapper>}

          {/* Main Content Area */}
          <div className="flex-1 min-w-0" data-id="element-21834" data-mp="2j9c7o1omv3tm">
            {/* FIND EVENTS TAB */}
            {activeTab === 'find' && <>
                {/* Sub-tabs and view toggle */}
                <div className="flex items-center justify-between mb-6" data-id="element-21835" data-mp="2szdr2g61jy27">
                  <div className={`flex border-b ${dk.border}`} data-id="element-21836" data-mp="2t1htsus7er0w">
                    {findSubTabs.map(tab => <button key={tab.id} onClick={() => setFindSubTab(tab.id)} className={`px-4 py-2 font-medium text-sm mr-4 transition-colors ${findSubTab === tab.id ? `${rt.textPrimary} border-b-2 ${rt.borderAccent}` : `${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-21837" data-mp="20aq3gdxtkwlh">
                        {tab.label}
                      </button>)}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-21838" data-mp="3lq5mhbfa8ioh">
                    <span className={`text-sm ${dk.textMuted}`} data-id="element-21839" data-mp="3gog7jkoxvkv4">
                      {filteredEvents.length} events
                    </span>
                    <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? `${rt.textPrimary} ${rt.bgMedium}` : dk.textFaint}`} data-id="element-21840" data-mp="3ob3iijyaboqk">
                      <LayoutListIcon className="h-5 w-5" data-id="element-21841" data-mp="3jafk3d7q85mb" />
                    </button>
                    <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? `${rt.textPrimary} ${rt.bgMedium}` : dk.textFaint}`} data-id="element-21842" data-mp="auvuco69l5c0">
                      <LayoutGridIcon className="h-5 w-5" data-id="element-21843" data-mp="1zpvk95evniz" />
                    </button>
                  </div>
                </div>

                {/* Events List/Grid */}
                {viewMode === 'list' ? <div className="space-y-6" data-id="element-21844" data-mp="yj6rbg8uakv0">
                    {filteredEvents.map(event => {
                const daysRemaining = getDaysRemaining(event.applicationDeadline);
                const isInterested = interestedEvents.includes(event.id);
                const deadlinePassed = daysRemaining <= 0;
                return <div key={event.id} className={`rounded-lg shadow-md overflow-hidden border ${dk.card}`} data-id="element-21845" data-mp="ugflmhggohen">
                          <div className="md:flex" data-id="element-21846" data-mp="kpb87m6fp4lx">
                            <div className="md:w-1/3 h-48 md:h-auto relative" data-id="element-21847" data-mp="kojn55agqp9w">
                              <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-21848" data-mp="gp0z40nchy2u" />
                              <div className="absolute top-2 right-2 bg-white bg-opacity-90 rounded-full px-3 py-1 text-sm font-medium flex items-center text-gray-800" data-id="element-21849" data-mp="6rwcy0ti22m1">
                                <UsersIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-21850" data-mp="3nrwm6erql4hm" />
                                {event.expectedAttendance.toLocaleString()}
                              </div>
                              <div className={`absolute bottom-2 left-2 text-white rounded-full px-3 py-1 text-sm font-medium ${rt.bgDark}`} data-id="element-21851" data-mp="6kkobloq375w">
                                {event.eventType}
                              </div>
                            </div>
                            <div className="p-6 md:w-2/3" data-id="element-21852" data-mp="3eyjichs4wigr">
                              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4" data-id="element-21853" data-mp="3jrcnh6rilstd">
                                <div data-id="element-21854" data-mp="2madwfrpcgnfc">
                                  <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-21855" data-mp="2m9ckll0i7wwk">
                                    {event.title}
                                  </h2>
                                  <div className={`flex items-center mt-2 text-sm ${dk.textMuted}`} data-id="element-21856" data-mp="5ask2fz8wwp3">
                                    <CalendarIcon className="h-4 w-4 mr-2" data-id="element-21857" data-mp="eap3o36s35da" />
                                    {formatDate(event.date)}
                                  </div>
                                  <div className={`flex items-center mt-1 text-sm ${dk.textMuted}`} data-id="element-21858" data-mp="s8dl111mzudl">
                                    <MapPinIcon className="h-4 w-4 mr-2" data-id="element-21859" data-mp="11zrrxtvx3wgr" />
                                    {event.location}
                                  </div>
                                </div>
                                <div className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${deadlinePassed ? 'bg-red-100 text-red-800' : daysRemaining <= 14 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-21860" data-mp="1byz2k9b9pd06">
                                  <ClockIcon className="h-4 w-4 mr-1" data-id="element-21861" data-mp="1ukf2os971pyf" />
                                  {deadlinePassed ? 'Closed' : daysRemaining <= 14 ? `${daysRemaining} days left` : `${daysRemaining} days to apply`}
                                </div>
                              </div>

                              <p className={`mb-4 ${dk.textSecondary} line-clamp-2`} data-id="element-21862" data-mp="xxa41vefktv0">
                                {event.description}
                              </p>

                              <div className="flex flex-wrap gap-4 mb-4" data-id="element-21863" data-mp="nz2zyd58q6af">
                                <div className={`flex items-center text-sm ${dk.textMuted}`} data-id="element-21864" data-mp="swcpdssmqhy9">
                                  <DollarSignIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-21865" data-mp="trtdi4ko4gz9" />
                                  {event.boothPriceRange}
                                </div>
                                <div className={`flex items-center text-sm ${dk.textMuted}`} data-id="element-21866" data-mp="12u2oevjumcd6">
                                  <UsersIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-21867" data-mp="1cq5n1rfhrsvd" />
                                  {event.interestedVendors} interested
                                </div>
                              </div>

                              <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t ${dk.border}`} data-id="element-21868" data-mp="1qjddvgvr0kc0">
                                <div className={`text-sm ${dk.textMuted}`} data-id="element-21869" data-mp="3g08z7eg2344s">
                                  <span className="font-medium" data-id="element-21870" data-mp="3azuykhgsawgs">Apply by:</span>{' '}
                                  {formatDate(event.applicationDeadline)}
                                </div>
                                <button onClick={() => toggleInterest(event.id)} disabled={deadlinePassed} className={`px-4 py-2 rounded-md text-sm font-medium flex items-center text-white transition-colors ${deadlinePassed ? 'bg-gray-300 cursor-not-allowed' : isInterested ? 'bg-green-600 hover:bg-green-700' : rt.primaryButton}`} data-id="element-21871" data-mp="3207xjwryvso9">
                                  {isInterested ? <>
                                      <CheckIcon className="h-4 w-4 mr-2" data-id="element-21872" data-mp="madbxtyih8js" />
                                      Applied
                                    </> : <>
                                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-21873" data-mp="15df4i1us7agg" />
                                      Express Interest
                                    </>}
                                </button>
                              </div>

                              {/* Operator info */}
                              <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`} data-id="element-21874" data-mp="hm0083jg2j2k">
                                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm" data-id="element-21875" data-mp="8ny3w1anoelm">
                                  <span className={dk.textMuted} data-id="element-21876" data-mp="cnq3vplzhcfd">
                                    <span className="font-medium" data-id="element-21877" data-mp="230xpcb1pw30t">
                                      Organizer:
                                    </span>{' '}
                                    {event.operator.organization}
                                  </span>
                                  <span className={dk.textMuted} data-id="element-21878" data-mp="3rktgbakw3h5u">
                                    <span className="font-medium" data-id="element-21879" data-mp="3njzrmezmhybw">
                                      Contact:
                                    </span>{' '}
                                    {event.operator.name}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>;
              })}

                    {filteredEvents.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-21880" data-mp="o3baf8oogk82">
                        <CalendarIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-21881" data-mp="2puoc7ovcmbzw" />
                        <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-21882" data-mp="2kxxwj2nghif0">
                          No events found
                        </h3>
                        <p className={dk.textMuted} data-id="element-21883" data-mp="2reg8jv7sq2dz">
                          Try adjusting your search or filters
                        </p>
                      </div>}
                  </div> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-21884" data-mp="23n2cpfafroo9">
                    {filteredEvents.map(event => {
                const daysRemaining = getDaysRemaining(event.applicationDeadline);
                const deadlinePassed = daysRemaining <= 0;
                const city = event.location.split(',')[0];
                return <div key={event.id} className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer" data-id="element-21885" data-mp="1kvrqvcjki238">
                          <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-21886" data-mp="1i9sfhncinndm" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-21887" data-mp="13em8oy5ljkj8" />
                          <div className="absolute top-2 right-2 z-10" data-id="element-21888" data-mp="zf3l1ume4vcp">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full text-white ${rt.bgDark}`} data-id="element-21889" data-mp="ukpp9r3tgm7i">
                              {event.eventType}
                            </span>
                          </div>
                          <div className="absolute top-2 left-2 z-10" data-id="element-21890" data-mp="fnq9hfyt9n1y">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${deadlinePassed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-id="element-21891" data-mp="bmwmqpx3uean">
                              {deadlinePassed ? 'Closed' : 'Open'}
                            </span>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-21892" data-mp="3o93jwrhkilkw">
                            <h3 className="text-lg font-semibold leading-tight mb-1" data-id="element-21893" data-mp="krd5f9w9n5hq">
                              {event.title}
                            </h3>
                            <div className="flex items-center text-sm" data-id="element-21894" data-mp="b0qs5mc0ur27">
                              <MapPinIcon className="h-4 w-4 mr-1" data-id="element-21895" data-mp="ayecxgxneusu" />
                              {city}
                            </div>
                          </div>
                        </div>;
              })}
                  </div>}
              </>}

            {/* MY EVENTS TAB */}
            {activeTab === 'myevents' && <div className="space-y-6" data-id="element-21896" data-mp="1hsvaxqgzru4z">
                <div className="flex items-center justify-between mb-4" data-id="element-21897" data-mp="1dtmkauh90cdn">
                  <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-21898" data-mp="1dra2chbhq20l">
                    My Event Applications
                  </h2>
                  <Link to="/vendors/event-manager" className={`px-4 py-2 rounded-lg text-sm font-medium ${rt.primaryButton}`} data-id="element-21899" data-mp="1ml4zp36deo5o">
                    <PlusIcon className="h-4 w-4 inline mr-2" data-id="element-21900" data-mp="uuqoxftnlry8" />
                    Create Event
                  </Link>
                </div>

                {myEvents.map(event => <div key={event.id} className={`rounded-lg shadow-sm border p-4 ${dk.card}`} data-id="element-21901" data-mp="13v6qgjqu6p0g">
                    <div className="flex gap-4" data-id="element-21902" data-mp="2k7c3wbyriehi">
                      <img src={event.image} alt={event.title} className="w-24 h-24 object-cover rounded-lg flex-shrink-0" data-id="element-21903" data-mp="2falntomyjiw3" />
                      <div className="flex-1 min-w-0" data-id="element-21904" data-mp="hnjiwsvuzq16">
                        <div className="flex items-start justify-between gap-4" data-id="element-21905" data-mp="mll0tahjtjb2">
                          <div data-id="element-21906" data-mp="mji09wirs7s3">
                            <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-21907" data-mp="rc1gl18udh0l">
                              {event.title}
                            </h3>
                            <div className={`flex items-center mt-1 text-sm ${dk.textMuted}`} data-id="element-21908" data-mp="wb4m0msqhp93">
                              <CalendarIcon className="h-4 w-4 mr-1" data-id="element-21909" data-mp="15bud5d71urgh" />
                              {formatDate(event.date)}
                            </div>
                            <div className={`flex items-center mt-1 text-sm ${dk.textMuted}`} data-id="element-21910" data-mp="1a8t5prb2eqck">
                              <MapPinIcon className="h-4 w-4 mr-1" data-id="element-21911" data-mp="3c3ld8jpiset5" />
                              {event.location}
                            </div>
                          </div>
                          <span className={`px-3 py-1 ${rt.badgeRadius} text-xs font-medium capitalize ${getStatusBadgeClasses(event.status)}`} data-id="element-21912" data-mp="2zrsqnum8nmzt">
                            {event.status}
                          </span>
                        </div>

                        <div className={`mt-3 flex flex-wrap gap-4 text-sm ${dk.textSecondary}`} data-id="element-21913" data-mp="3ni59i0j0v456">
                          <span data-id="element-21914" data-mp="3igpkxyhmyfi1">
                            <span className="font-medium" data-id="element-21915" data-mp="3dj69158euet9">Booth:</span>{' '}
                            {event.boothType}
                          </span>
                          <span data-id="element-21916" data-mp="39huh2t22dhhw">
                            <span className="font-medium" data-id="element-21917" data-mp="224ajupc50uyg">Total:</span> $
                            {event.totalCost}
                          </span>
                        </div>

                        <div className="mt-3 flex gap-2" data-id="element-21918" data-mp="1y5bj7gxfvkct">
                          <button className={`px-3 py-1.5 rounded text-sm font-medium ${rt.bgLight} ${rt.textPrimary}`} data-id="element-21919" data-mp="1qi67lp7ou4pa">
                            View Details
                          </button>
                          {event.status === 'applied' && <button className="px-3 py-1.5 rounded text-sm font-medium text-red-600 hover:bg-red-50" data-id="element-21920" data-mp="1cj6fu8ovp9ow">
                              Cancel
                            </button>}
                          <button className={`px-3 py-1.5 rounded text-sm font-medium ${dk.textMuted} hover:${dk.surfaceMuted}`} data-id="element-21921" data-mp="12rsctqxpxrr0">
                            <MailIcon className="h-4 w-4 inline mr-1" data-id="element-21922" data-mp="tv1k9oidkv16" />
                            Contact
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>}

            {/* CALENDAR TAB */}
            {activeTab === 'calendar' && <div className={`rounded-xl shadow-sm overflow-hidden ${dk.card}`} data-id="element-21923" data-mp="30ge7r509za0w">
                <div className={`p-4 border-b flex justify-between items-center ${dk.border}`} data-id="element-21924" data-mp="35h1yrqoi7qv4">
                  <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-21925" data-mp="39ft06a005uo1">
                    Event Calendar
                  </h2>
                  <div className="flex items-center space-x-4" data-id="element-21926" data-mp="2hq6d7ban5rwb">
                    <button onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(currentDate.getMonth() - 1);
                  setCurrentDate(newDate);
                }} className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} data-id="element-21927" data-mp="2holfxdwxzuur">
                      <ChevronLeftIcon className="h-5 w-5" data-id="element-21928" data-mp="31dmftcv8hv2h" />
                    </button>
                    <span className={`text-lg font-medium min-w-[160px] text-center ${dk.textPrimary}`} data-id="element-21929" data-mp="2wet6zxqw6s8n">
                      {currentDate.toLocaleString('default', {
                    month: 'long',
                    year: 'numeric'
                  })}
                    </span>
                    <button onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(currentDate.getMonth() + 1);
                  setCurrentDate(newDate);
                }} className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} data-id="element-21930" data-mp="2iftgekjtk39u">
                      <ChevronRightIcon className="h-5 w-5" data-id="element-21931" data-mp="6no6bqg6svwi" />
                    </button>
                  </div>
                </div>

                <div className="p-4" data-id="element-21932" data-mp="a7olkdpqquph">
                  <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-21933" data-mp="a6lvu945myb5">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className={`text-sm font-medium text-center p-2 ${dk.textMuted}`} data-id="element-21934" data-mp="a0ovi8krd4o">
                          {day}
                        </div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1" data-id="element-21935" data-mp="xvy333fx8wat">
                    {Array.from({
                  length: getFirstDayOfMonth(currentDate)
                }).map((_, i) => <div key={`empty-${i}`} className={`min-h-[80px] ${darkMode ? 'bg-gray-900/30' : 'bg-gray-50'}`} data-id="element-21936" data-mp="o64qq07ygd5i" data-mpi={i} />)}
                    {Array.from({
                  length: getDaysInMonth(currentDate)
                }).map((_, i) => {
                  const day = i + 1;
                  const dateString = formatDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const {
                    myEvents: eventsOnDay,
                    deadlines
                  } = getEventsForCalendarDate(dateString);
                  const isSelected = selectedCalendarDate === dateString;
                  const hasItems = eventsOnDay.length > 0 || deadlines.length > 0;
                  return <div key={day} onClick={() => setSelectedCalendarDate(dateString)} className={`min-h-[80px] p-2 border cursor-pointer transition-colors ${darkMode ? `border-gray-700 ${isSelected ? 'ring-2 ring-purple-500 bg-gray-700' : 'hover:bg-gray-700/50'}` : `border-gray-200 ${isSelected ? 'ring-2 ring-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}`} data-id="element-21937" data-mp="1xhfosytwhhy" data-mpi={i}>
                          <span className={`text-sm ${dk.textMuted}`} data-id="element-21938" data-mp="12061h08wb6sk">
                            {day}
                          </span>
                          {hasItems && <div className="mt-1 flex gap-1" data-id="element-21939" data-mp="1byv81wvcd4ti">
                              {eventsOnDay.length > 0 && <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-21940" data-mp="1gwoemr0k40ub" />}
                              {deadlines.length > 0 && <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-21941" data-mp="1pq9u6ckjpm4s" />}
                            </div>}
                        </div>;
                })}
                  </div>
                </div>

                {/* Legend */}
                <div className={`px-4 pb-4 flex gap-4 text-sm ${dk.textMuted}`} data-id="element-21942" data-mp="24mr4r1kwuwqa">
                  <div className="flex items-center gap-2" data-id="element-21943" data-mp="1ez59hrfwagtp">
                    <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-21944" data-mp="19ziupdkf4sis" />
                    <span data-id="element-21945" data-mp="19xftryhq9eza">My Events</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-21946" data-mp="1ooyickonkhu9">
                    <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-21947" data-mp="1jxq5feo46ecc" />
                    <span data-id="element-21948" data-mp="1jvlvaezmgj3m">Application Deadlines</span>
                  </div>
                </div>

                {/* Selected date details */}
                {selectedCalendarDate && <div className={`border-t p-4 ${dk.border}`} data-id="element-21949" data-mp="1srusro41h7n4">
                    <h3 className={`text-lg font-medium mb-4 ${dk.textHeading}`} data-id="element-21950" data-mp="27lpy4k4zketk">
                      {new Date(selectedCalendarDate).toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
                    </h3>
                    {(() => {
                const {
                  myEvents: eventsOnDay,
                  deadlines
                } = getEventsForCalendarDate(selectedCalendarDate);
                if (eventsOnDay.length === 0 && deadlines.length === 0) {
                  return <p className={dk.textMuted} data-id="element-21951" data-mp="216shtkip5ju1">No events on this date</p>;
                }
                return <div className="space-y-2" data-id="element-21952" data-mp="259xirin4pdgk">
                          {eventsOnDay.map(e => <div key={e.id} className={`p-3 rounded-lg ${rt.bgLight}`} data-id="element-21953" data-mp="1ra6197xelext">
                              <span className="text-xs font-medium text-green-600" data-id="element-21954" data-mp="3lnnlom2ihvom">
                                EVENT
                              </span>
                              <p className={`font-medium ${dk.textPrimary}`} data-id="element-21955" data-mp="2deuk111fsl5x">
                                {e.title}
                              </p>
                            </div>)}
                          {deadlines.map(e => <div key={e.id} className={`p-3 rounded-lg bg-amber-50`} data-id="element-21956" data-mp="24kprmd6nn1oa">
                              <span className="text-xs font-medium text-amber-600" data-id="element-21957" data-mp="1up5whszx070l">
                                DEADLINE
                              </span>
                              <p className={`font-medium ${dk.textPrimary}`} data-id="element-21958" data-mp="1lneoghhwjo3k">
                                {e.title}
                              </p>
                            </div>)}
                        </div>;
              })()}
                  </div>}
              </div>}

            {/* TICKETS TAB */}
            {activeTab === 'tickets' && <div className="space-y-6" data-id="element-21959" data-mp="3ilcdckuapkuv">
                <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-21960" data-mp="3nl8hck2ztcaf">
                  My Event Tickets
                </h2>

                {eventTickets.map(ticket => <div key={ticket.id} className={`rounded-lg shadow-sm border p-4 ${dk.card}`} data-id="element-21961" data-mp="3scqf6z9pywr6">
                    <div className="flex gap-4" data-id="element-21962" data-mp="2zo2tx3oz07dm">
                      <img src={ticket.image} alt={ticket.eventTitle} className="w-24 h-24 object-cover rounded-lg flex-shrink-0" data-id="element-21963" data-mp="2zpds8nzncmxc" />
                      <div className="flex-1 min-w-0" data-id="element-21964" data-mp="3dmsr0jbsi71z">
                        <div className="flex items-start justify-between gap-4" data-id="element-21965" data-mp="3dodj82amtdwo">
                          <div data-id="element-21966" data-mp="ougg1t7due6a">
                            <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-21967" data-mp="oxb2trdvcb2v">
                              {ticket.eventTitle}
                            </h3>
                            <p className={`text-sm ${dk.textMuted}`} data-id="element-21968" data-mp="1jzxsbvdh2jgl">
                              {ticket.ticketType}
                            </p>
                            <div className={`flex items-center mt-2 text-sm ${dk.textMuted}`} data-id="element-21969" data-mp="1a8tbsr85eqjn">
                              <CalendarIcon className="h-4 w-4 mr-1" data-id="element-21970" data-mp="1f6bo8rldjnwe" />
                              {formatDate(ticket.eventDate)}
                            </div>
                          </div>
                          <span className={`px-3 py-1 ${rt.badgeRadius} text-xs font-medium capitalize ${getTicketStatusBadge(ticket.status)}`} data-id="element-21971" data-mp="167zs6a1qnxwk">
                            {ticket.status}
                          </span>
                        </div>

                        <div className={`mt-3 flex flex-wrap gap-4 text-sm ${dk.textSecondary}`} data-id="element-21972" data-mp="2lohzjkt87tli">
                          <span data-id="element-21973" data-mp="ezgahnvtggjf">
                            <span className="font-medium" data-id="element-21974" data-mp="f0hnhxwe7d3s">Purchased:</span>{' '}
                            {formatDate(ticket.purchaseDate)}
                          </span>
                          <span data-id="element-21975" data-mp="oq19vdjbdp3e">
                            <span className="font-medium" data-id="element-21976" data-mp="or3yfet5zfg5">Price:</span> $
                            {ticket.price}
                          </span>
                        </div>

                        <div className="mt-3 flex gap-2" data-id="element-21977" data-mp="xp65aazkx2bc">
                          {ticket.status === 'valid' && <>
                              <button className={`px-3 py-1.5 rounded text-sm font-medium ${rt.primaryButton}`} data-id="element-21978" data-mp="tqymzbpgvsy1">
                                <DownloadIcon className="h-4 w-4 inline mr-1" data-id="element-21979" data-mp="12q3lii113ma7" />
                                Download
                              </button>
                              <button className={`px-3 py-1.5 rounded text-sm font-medium ${rt.bgLight} ${rt.textPrimary}`} data-id="element-21980" data-mp="34hgqf72zxu69">
                                <QrCodeIcon className="h-4 w-4 inline mr-1" data-id="element-21981" data-mp="1dc3pch0vuryb" />
                                Show QR
                              </button>
                            </>}
                        </div>
                      </div>
                    </div>
                  </div>)}

                {eventTickets.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-21982" data-mp="1wbhl94i6acrg">
                    <TicketIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-21983" data-mp="213hhomz1anja" />
                    <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-21984" data-mp="255vyz2n21f8g">
                      No tickets yet
                    </h3>
                    <p className={dk.textMuted} data-id="element-21985" data-mp="2f28so0ilnt37">
                      Tickets you purchase for events will appear here
                    </p>
                  </div>}
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default VendorEventsHub;